// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Snapshot of the page the advisor is currently looking at. Sent to
 * intelligence-service alongside every chat message so Fin can resolve
 * "this relationship", "here", "this client" pronouns without the
 * advisor having to spell out which entity they mean.
 *
 * The context is per-message (not per-session) because the advisor
 * navigates while the session is live — a session opened on the
 * Dashboard and continued on the RelationshipDetailPage should carry
 * the current page on every turn.
 */
export type AdvisorPageType =
  | 'DASHBOARD'
  | 'RELATIONSHIPS_LIST'
  | 'RELATIONSHIP_DETAIL'
  | 'CASHFLOW_WORKSPACE'
  | 'CLIENT_DETAIL'
  | 'POLICY_DETAIL'
  | 'LOA_DETAIL'
  | 'COA_DETAIL'
  | 'REQUEST_DETAIL'
  | 'FUNDS'
  | 'FUND_DETAIL'
  | 'STRATEGY_DETAIL'
  | 'QUOTES'
  | 'QUOTE_DETAIL'
  | 'TODAY'
  | 'CALENDAR'
  | 'ACTIVITY'
  | 'SETTINGS'
  | 'UNKNOWN';

/**
 * Typed map of entity ids visible on the current page. Backend
 * validates each id is a UUID (or fund_key) and belongs to the
 * calling brokerage; unknown keys are dropped silently.
 */
export interface AdvisorPageEntityIds {
  profileId?: string;
  clientId?: string;
  policyId?: string;
  loaId?: string;
  coaId?: string;
  requestId?: string;
  /** Composite stable key, e.g. `aviva:GF3`. Not a UUID. */
  fundKey?: string;
  strategyId?: string;
  quoteId?: string;
  calendarEventId?: string;
  /** The open what-if cashflow (forecast) in the Cashflow Modelling workspace. */
  scenarioId?: string;
}

export interface AdvisorPageSelection {
  /** Entity type discriminator for the selected ids. */
  type: string;
  /** Selected entity ids. Backend caps the list at 50. */
  ids: string[];
}

export interface AdvisorPageContext {
  pageType: AdvisorPageType;
  entityIds?: AdvisorPageEntityIds;
  selection?: AdvisorPageSelection;
  /** Literal pathname (query string stripped). Audit only. */
  url: string;
  /** Short human label for the prompt block. Capped 200 chars; HTML stripped. */
  pageTitle?: string;
}

/** Default context for routes that have not declared one yet. */
export function unknownPageContext(url: string): AdvisorPageContext {
  return { pageType: 'UNKNOWN', url };
}
